import {
  Box,
  FormControl,
  TextField,
  styled,
  MenuItem,
  Typography,
  CircularProgress,
} from "@mui/material";
import React, { useState } from "react";
import { updatePosition } from "../../utils/updatePosition";

// Styled MenuItem for ellipsis with text wrapping
const EllipsisMenuItem = styled(MenuItem)(({ theme }) => ({
  fontSize: "10px",
  overflow: "hidden",
  whiteSpace: "normal", // Allow text to wrap
  textOverflow: "clip", // No text overflow, allow wrap
  width: "100%", // You can adjust this or use width: '100%' for flexible sizing
  wordBreak: "break-word", // Ensures that long words break correctly and wrap to the next line
}));

// CustomTextField styled to match input style
const CustomTextField = styled(TextField)(({ theme, error }) => ({
  width: "100%", // Ensures it takes full width
  "& .MuiInputBase-root": {
    height: "30px",
    p: 0,
    fontSize: "12px",
  },
  ...(error && {
    "& .MuiOutlinedInput-root": {
      borderColor: "#D34040",
      "&:hover": {
        borderColor: "#D34040",
      },
      "&.Mui-focused": {
        borderColor: "#D34040",
      },
    },
  }),
}));

const TableSelectField = ({
  defaultValue,
  newOption,
  title,
  value,
  handleChange,
  name,
  options,
  errors,
  height,
  disabled,
  field,
  keyName,
  disable,
}) => {
  const [newOptions, setNewOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [newOptionValue, setNewOptionValue] = useState("");
  const [isAdded, setIsAdded] = useState(false);

  // Customize MenuProps to control dropdown behavior
  const MenuProps = {
    PaperProps: {
      style: {
        maxHeight: "55%",
        overflowY: "auto",
      },
    },
  };

  const handleNewOption = (e) => {
    setNewOptionValue(e.target.value);
    handleChange(e);
  };
  // Handle adding new option
  const handleAddNew = () => {
    if (newOptionValue.trim()) {
      const updatedOptions = [...newOptions, newOptionValue]; // Add the new option
      setNewOptions(updatedOptions);
      setIsAdded(true); // Mark as added to prevent adding again
      setNewOptionValue(""); // Clear the input
      setIsAddingNew(false); // Close the input field
      handleChange({ target: { name, value: newOptionValue } }); // Automatically select the new option
    }
  };

  // Combine static options with new ones
  const allOptions = newOption
    ? updatePosition([
        ...(options || []), // Spread the existing options, fallback to empty array if undefined
        ...(newOptions || []), // Spread the fetched options
        ...(newOption ? [newOption] : []), // Add the new option value if it's not empty
      ])
    : options || newOptions;

  // Handle the change in value
  const handleOptionChange = (e) => {
    const selectedValue = e.target.value;
    if (selectedValue === "Others") {
      setIsAddingNew(true); // Show the input field when 'Others' is selected
      setNewOptionValue(""); // Clear previous input value
    } else {
      handleChange(e); // Regular option selected, pass the value back to parent
    }
  };

  return (
    <Box>
      {title && (
        <Typography variant="subtitle1" sx={{ fontWeight: "bold", mt: 1 }}>
          {title}
        </Typography>
      )}
      <FormControl fullWidth margin="normal" sx={{ mt: 0, p: 0 }}>
        {isAddingNew ? (
          // Show the TextField for custom input when 'Other' is selected
          <Box>
            <CustomTextField
              value={newOptionValue}
              height={height}
              name={name}
              onChange={handleNewOption}
              fullWidth
              placeholder="New Option"
              SelectProps={{ MenuProps }}
              sx={{ fontSize: "14px" }}
            />
          </Box>
        ) : (
          // Show the select dropdown if 'Other' is not selected
          <CustomTextField
            sx={{ fontSize: "14px" }}
            select
            height={height}
            name={name}
            value={value || "Tap to select"} // Default to "Tap to select" if no value is set
            onChange={handleOptionChange}
            error={!!errors}
            SelectProps={{ MenuProps }}
          >
            <MenuItem value="Tap to select" sx={{ fontSize: "14px" }}>
              {defaultValue || "Tap to select"}
            </MenuItem>

            {loading ? (
              <MenuItem disabled>
                <CircularProgress size={24} />
              </MenuItem>
            ) : (
              allOptions.map((option, index) => {
                return option === "Others" ? (
                  <EllipsisMenuItem
                    sx={{ fontSize: "14px" }}
                    key={index}
                    value="Others"
                    onClick={() => setIsAddingNew(true)} // Trigger the input field when 'Others' is clicked
                  >
                    Other (specify)
                  </EllipsisMenuItem>
                ) : (
                  <EllipsisMenuItem
                    sx={{ fontSize: "14px" }}
                    key={option?.id || index}
                    disabled={disable===option}
                    value={option?.id || option}

                  >
                    {option?.name || option?.month || option}{" "}
                    {/* Render the option's name */}
                  </EllipsisMenuItem>
                );
              })
            )}
          </CustomTextField>
        )}
        {errors && (
          <Typography sx={{ fontSize: "12px" }} color="#D34040">
            {errors}
          </Typography>
        )}
      </FormControl>
    </Box>
  );
};

export default TableSelectField;
